import { getDomain, getEnvironment, isDev } from '@library/environment/isomorphic'
import { composeFetch } from '@library/http'
import {
  addPath,
  composeRequest,
  goustoHeaders,
  sendJSON,
  setHost,
  setMethod,
} from '@library/http/requests'

import type {
  DashboardExperienceData,
  DashboardExperienceMeta,
  DashboardExperienceResponse,
  LandingExperienceData,
  LandingExperienceResponse,
  OrderConfirmationBanner,
} from './interfaces'

const LOCAL_API_HOST = 'https://staging-api.gousto.info'

function getActivateApiHost() {
  if (isDev()) {
    return LOCAL_API_HOST
  }

  return `https://${getEnvironment()}-api.${getDomain()}`
}

const parseLandingExperience = async (response: Response): Promise<LandingExperienceResponse> => {
  if (response.status === 204) {
    return { status: 204, data: null }
  }

  if (!response.ok) {
    const responseMessage = await response.text()
    throw new Error(
      `landing-experience service responded with ${response.status}: ${responseMessage}`,
    )
  }

  const body = (await response.json()) as { data: LandingExperienceData }
  return { status: 200, data: body.data }
}

export const fetchLandingExperience = composeFetch(
  composeRequest(
    setHost(getActivateApiHost),
    addPath('ux/engagement/v1/landing-experience'),
    setMethod('GET'),
    goustoHeaders,
  ),
  parseLandingExperience,
)

const parseDashboardExperience = async (
  response: Response,
): Promise<DashboardExperienceResponse> => {
  if (response.status === 204) {
    return { status: 204, data: null }
  }

  if (!response.ok) {
    const responseMessage = await response.text()
    throw new Error(
      `dashboard-experience service responded with ${response.status}: ${responseMessage}`,
    )
  }

  const body = (await response.json()) as {
    data: DashboardExperienceData
    meta?: DashboardExperienceMeta
  }

  // Optional chaining keeps a missing meta/trackingEvents/pageLoad a no-op (undefined)
  // rather than an error — an un-instrumented response must never break the dashboard.
  return { status: 200, data: body.data, pageLoad: body.meta?.trackingEvents?.pageLoad }
}

export const fetchDashboardExperience = composeFetch(
  composeRequest(
    setHost(getActivateApiHost),
    addPath('ux/engagement/v1/dashboard'),
    setMethod('GET'),
    goustoHeaders,
  ),
  parseDashboardExperience,
)

type OrderConfirmationBannersInput = { orderId: string }

const VALID_RAF_STEP_STATUSES = new Set(['completed', 'current', 'upcoming'])

const isValidStep = (step: unknown): boolean => {
  if (typeof step !== 'object' || step === null) {
    return false
  }

  const { label, status } = step as Record<string, unknown>
  return (
    typeof label === 'string' && typeof status === 'string' && VALID_RAF_STEP_STATUSES.has(status)
  )
}

// A malformed entry must be dropped, not passed through, so the caller falls back to the
// legacy module instead of hanging in 'loading' or throwing on a missing nested field.
const isValidOrderConfirmationBanner = (item: unknown): item is OrderConfirmationBanner => {
  if (typeof item !== 'object' || item === null) {
    return false
  }

  const banner = item as Record<string, unknown>
  if (banner.type !== 'raf_stepper' || typeof banner.data !== 'object' || banner.data === null) {
    return false
  }

  const data = banner.data as Record<string, unknown>
  if (
    typeof data.title !== 'string' ||
    typeof data.description !== 'string' ||
    typeof data.creditFormatted !== 'string' ||
    typeof data.footerText !== 'string' ||
    typeof data.action !== 'object' ||
    data.action === null
  ) {
    return false
  }

  if (data.steps !== undefined && data.steps !== null) {
    if (!Array.isArray(data.steps) || !data.steps.every(isValidStep)) {
      return false
    }
  }

  const action = data.action as Record<string, unknown>
  if (typeof action.intent !== 'string' || typeof action.title !== 'string') {
    return false
  }

  if (
    action.webTitle !== undefined &&
    action.webTitle !== null &&
    typeof action.webTitle !== 'string'
  ) {
    return false
  }

  const { successMessage } = action
  if (successMessage === undefined) {
    return true
  }

  if (typeof successMessage !== 'object' || successMessage === null) {
    return false
  }

  const message = successMessage as Record<string, unknown>
  return (
    typeof message.title === 'string' &&
    typeof message.subtitle === 'string' &&
    typeof message.linkText === 'string'
  )
}

const parseOrderConfirmationBanners = async (
  response: Response,
): Promise<OrderConfirmationBanner[]> => {
  if (!response.ok) {
    const responseMessage = await response.text()
    throw new Error(
      `order-confirmation-banners service responded with ${response.status}: ${responseMessage}`,
    )
  }

  const body = (await response.json()) as { data?: unknown }
  return Array.isArray(body.data) ? body.data.filter(isValidOrderConfirmationBanner) : []
}

const getOrderConfirmationBanners = composeFetch(
  composeRequest<OrderConfirmationBannersInput>(
    setHost(getActivateApiHost),
    addPath((input) => `ux/engagement/v1/order-confirmation-banners/${input.orderId}`),
    setMethod('GET'),
    goustoHeaders,
  ),
  parseOrderConfirmationBanners,
)

/** Banners to render on Order Confirmation for this order — empty when none apply. */
export const fetchOrderConfirmationBanners = (
  orderId: string,
): Promise<OrderConfirmationBanner[]> => getOrderConfirmationBanners({ orderId })

// Write endpoints return 204 No Content on success; any non-2xx is an error.
const parseNoContent = async (response: Response): Promise<void> => {
  if (!response.ok) {
    const responseMessage = await response.text()
    throw new Error(
      `engagement orders service responded with ${response.status}: ${responseMessage}`,
    )
  }
}

type EditOrderDeliveryInput = {
  data: {
    type: 'order'
    id: string
    attributes: {
      delivery_day_id: string
      delivery_slot_id: string
      day_slot_lead_time_id: string
    }
  }
}

type EditOrderDeliverySelection = {
  deliveryDayId: string
  deliverySlotId: string
  daySlotLeadTimeId: string
}

const patchOrderDelivery = composeFetch(
  composeRequest<EditOrderDeliveryInput>(
    setHost(getActivateApiHost),
    addPath((input) => `ux/engagement/v1/orders/${input.data.id}`),
    setMethod('PATCH'),
    sendJSON<EditOrderDeliveryInput>(),
    goustoHeaders,
  ),
  parseNoContent,
)

/**
 * Change an upcoming order's delivery day/slot. All three ids come from the chosen
 * `details.available_delivery_days[]` entry: `deliveryDayId` is the day's `id`,
 * `deliverySlotId` is the slot's `id`, and `daySlotLeadTimeId` is the slot's
 * `day_slot_lead_time_id`.
 */
export const editOrderDelivery = (
  orderId: string,
  { deliveryDayId, deliverySlotId, daySlotLeadTimeId }: EditOrderDeliverySelection,
): Promise<void> =>
  patchOrderDelivery({
    data: {
      type: 'order',
      id: orderId,
      attributes: {
        delivery_day_id: deliveryDayId,
        delivery_slot_id: deliverySlotId,
        day_slot_lead_time_id: daySlotLeadTimeId,
      },
    },
  })

type CancelOrderInput = { orderId: string }

const deleteOrder = composeFetch(
  composeRequest<CancelOrderInput>(
    setHost(getActivateApiHost),
    addPath((input) => `ux/engagement/v1/orders/${input.orderId}`),
    setMethod('DELETE'),
    goustoHeaders,
  ),
  parseNoContent,
)

/** Cancel (skip) an upcoming order. */
export const cancelOrder = (orderId: string): Promise<void> => deleteOrder({ orderId })
